
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ProductInterestCTAProps {
  productName?: string;
  variant?: 'default' | 'compact' | 'inline';
}

export default function ProductInterestCTA({ productName, variant = 'default' }: ProductInterestCTAProps) {
  const { t } = useTranslation();

  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <i className="ri-building-2-line text-2xl text-amber-600"></i>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {t('common:productCTA.inlineTitle')}
          </p>
        </div>
        <Link
          to="/dealer-registration"
          className="px-4 py-2 bg-amber-500 text-black text-sm font-semibold rounded-full hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('common:productCTA.becomeDealer')}
        </Link>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-lock-line text-amber-500"></i>
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-500">
            {t('common:productCTA.dealerOnly')}
          </span>
        </div>
        <p className="text-sm text-gray-300 mb-4">
          {productName 
            ? t('common:productCTA.interestedInProduct', { product: productName })
            : t('common:productCTA.interestedGeneral')
          }
        </p>
        <Link
          to="/dealer-registration"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-black text-sm font-semibold rounded-full hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('common:productCTA.becomeDealer')}
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 md:p-10">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
          <i className="ri-building-2-line text-xl text-amber-500"></i>
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-500">
          {t('common:productCTA.b2bOnly')}
        </span>
      </div>

      <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
        {productName 
          ? t('common:productCTA.titleWithProduct', { product: productName })
          : t('common:productCTA.title')
        }
      </h3>

      <p className="text-gray-400 mb-6 leading-relaxed">
        {t('common:productCTA.description')}
      </p>

      <ul className="space-y-3 mb-8">
        {[1, 2, 3].map((num) => (
          <li key={num} className="flex items-center gap-3 text-sm text-gray-300">
            <i className="ri-checkbox-circle-fill text-amber-500"></i>
            <span>{t(`common:productCTA.benefit${num}`)}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/dealer-registration"
        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
      >
        {t('common:productCTA.ctaButton')}
        <i className="ri-arrow-right-line"></i>
      </Link>

      <p className="mt-4 text-xs text-gray-500">
        {t('common:productCTA.disclaimer')}
      </p>
    </div>
  );
}
